import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import SearchBar from "../components/SearchBar";
import { FaStar, FaMapMarkerAlt } from "react-icons/fa";
import "../styles/ModernPages.css";
import usePageMeta from "../hooks/usePageMeta";
import { pageTitle, breadcrumbSchema } from "../lib/seo";

function HotelsNew() {
  usePageMeta({
    title: pageTitle("Hotel Booking in India & Abroad"),
    description: "Book handpicked hotels in Nainital, Dalhousie, McLeod Ganj, Amritsar, Varanasi, Delhi, Singapore and more. Compare star ratings, amenities and nightly prices.",
    jsonLd: breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Hotels", path: "/hotels" }]),
  });
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [sortBy, setSortBy] = useState("featured");

  useEffect(() => {
    fetchHotels();
  }, []);

  async function fetchHotels() {
    try {
      const { data } = await supabase
        .from("hotels")
        .select("*")
        .order("created_at", { ascending: false });
      setHotels(data || []);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }

  const filteredHotels = useMemo(() => {
    let result = [...hotels];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (h) =>
          h.name?.toLowerCase().includes(query) ||
          h.city?.toLowerCase().includes(query)
      );
    }

    if (ratingFilter !== "all") {
      result = result.filter((h) => (h.stars || h.rating || 0) >= Number(ratingFilter));
    }

    if (sortBy === "priceLow") {
      result.sort((a, b) => (a.price_per_night || 0) - (b.price_per_night || 0));
    } else if (sortBy === "priceHigh") {
      result.sort((a, b) => (b.price_per_night || 0) - (a.price_per_night || 0));
    } else if (sortBy === "rating") {
      result.sort((a, b) => (b.stars || b.rating || 0) - (a.stars || a.rating || 0));
    }

    return result;
  }, [hotels, searchQuery, ratingFilter, sortBy]);

  function formatPrice(price) {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(price);
  }

  return (
    <div className="hotels-new-page">
      <div className="page-hero">
        <h1>Find Your Perfect Stay</h1>
        <p>Discover handpicked hotels and resorts for unforgettable experiences</p>
      </div>

      <div className="search-section">
        <div className="search-container">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by hotel name or city..."
          />
        </div>
      </div>

      <div className="content-container">
        <div className="filters-bar">
          <select
            className="filter-select"
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value)}
          >
            <option value="all">All Ratings</option>
            <option value="4.5">4.5+ Stars</option>
            <option value="4.0">4.0+ Stars</option>
            <option value="3.5">3.5+ Stars</option>
          </select>

          <select
            className="filter-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="featured">Featured</option>
            <option value="priceLow">Price: Low to High</option>
            <option value="priceHigh">Price: High to Low</option>
            <option value="rating">Highest Rated</option>
          </select>
        </div>

        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        )}

        {!loading && filteredHotels.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">🏨</div>
            <h3>No hotels found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        )}

        {!loading && filteredHotels.length > 0 && (
          <>
            <div className="cards-grid">
              {filteredHotels.map((hotel) => (
                <Link
                  key={hotel.id}
                  className="modern-card"
                  to={`/hotels/${hotel.id}/`}
                >
                  <div className="card-image">
                    <img
                      src={hotel.image || "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600"}
                      alt={hotel.name}
                      loading="lazy"
                    />
                    <div className="card-badge">
                      <FaStar style={{ marginRight: 4 }} />
                      {hotel.stars || hotel.rating || 4.5}
                    </div>
                  </div>

                  <div className="card-content">
                    <div className="card-location">
                      <FaMapMarkerAlt style={{ display: "inline", marginRight: 6 }} />
                      {hotel.city}, {hotel.country || "India"}
                    </div>

                    <h3 className="card-title">{hotel.name}</h3>

                    <div className="card-footer">
                      <div className="card-price">
                        <div className="price-amount">{formatPrice(hotel.price_per_night || 0)}</div>
                        <div className="price-label">per night</div>
                      </div>
                      <span className="card-cta">View Details</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div style={{ textAlign: "center", marginTop: 40, color: "#64748b" }}>
              Showing {filteredHotels.length} hotel{filteredHotels.length !== 1 ? "s" : ""}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default HotelsNew;